import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import api from '../../lib/api';

const STARTERS = [
  'Should I pay PTPTN or invest first?',
  'How much EPF voluntary contribution makes sense?',
  'How do I start investing with RM500?',
];

export default function ChatTab({ me, onUpgrade, onUsage }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState({ used: me?.chat_used ?? 0, limit: me?.chat_limit ?? null });
  const [limitHit, setLimitHit] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/chat/history').then(({ data }) => setMessages(data.messages)).catch(console.error);
  }, []);

  useEffect(() => {
    if (me) setUsage({ used: me.chat_used, limit: me.chat_limit });
  }, [me]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || sending) return;
    setInput('');
    setSending(true);
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: 'user', content: message }]);
    try {
      const { data } = await api.post('/chat', { message });
      setMessages((prev) => [...prev, { id: `tmp-a-${Date.now()}`, role: 'assistant', content: data.reply }]);
      setUsage({ used: data.chat_used, limit: data.chat_limit });
      onUsage?.();
    } catch (e) {
      if (e.response?.status === 402) {
        setLimitHit(true);
        setMessages((prev) => prev.slice(0, -1));
      } else {
        setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: "Sorry, I couldn't respond right now. Please try again." }]);
      }
    } finally {
      setSending(false);
    }
  };

  const atLimit = limitHit || (usage.limit !== null && usage.used >= usage.limit);

  return (
    <div className="bg-white border border-[#E5E5EB] rounded-2xl flex flex-col" style={{ height: 'min(70vh, 640px)' }}>
      <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[#E5E5EB]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#534AB7] flex items-center justify-center">
            <Sparkles size={15} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-[#0A0A0B] text-sm sm:text-base leading-tight">Ask Clarity</h3>
            <p className="text-xs text-[#A1A1AA]">Your Malaysian financial advisor</p>
          </div>
        </div>
        {usage.limit !== null && (
          <span data-testid="chat-usage-counter" className={`text-xs font-semibold rounded-full px-3 py-1.5 ${atLimit ? 'bg-red-50 text-red-600' : 'bg-[#F6F6F9] text-[#5C5C66]'}`}>
            {Math.min(usage.used, usage.limit)}/{usage.limit} free messages
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-[#5C5C66] mb-5">Ask me anything about your money — PTPTN, EPF, investing, budgeting. I know your numbers.</p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2">
              {STARTERS.map((q) => (
                <button
                  key={q}
                  data-testid="chat-starter-question"
                  onClick={() => send(q)}
                  disabled={atLimit || sending}
                  className="text-sm text-[#534AB7] bg-[#534AB7]/5 hover:bg-[#534AB7]/10 border border-[#534AB7]/20 rounded-full px-4 py-2.5 transition-colors disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              data-testid={`chat-message-${m.role}`}
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm sm:text-base leading-relaxed whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-[#534AB7] text-white rounded-br-md' : 'bg-[#F6F6F9] text-[#0A0A0B] rounded-bl-md'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#F6F6F9] rounded-2xl rounded-bl-md px-4 py-3 text-sm text-[#A1A1AA]">Clarity is thinking...</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {atLimit ? (
        <div data-testid="chat-limit-banner" className="border-t border-[#E5E5EB] px-5 sm:px-6 py-4 bg-[#534AB7]/5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-[#0A0A0B] font-medium text-center sm:text-left">
            You've used all 5 free messages this month. Go unlimited with Premium.
          </p>
          <button
            data-testid="chat-upgrade-btn"
            onClick={onUpgrade}
            className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-5 py-2.5 font-semibold text-sm whitespace-nowrap"
          >
            Upgrade Now
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#E5E5EB] px-4 sm:px-5 py-3.5 flex items-center gap-3"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about PTPTN, EPF, investing..."
            className="flex-1 bg-[#F6F6F9] border border-transparent rounded-xl px-4 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:bg-white transition-all"
          />
          <button
            data-testid="chat-send-btn"
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl p-3 disabled:opacity-40 transition-colors flex-shrink-0"
          >
            <Send size={18} />
          </button>
        </form>
      )}
    </div>
  );
}
